'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in the Placenta: The 2024 UNM Study Explained"
      author="MicroPlastics Editorial Team"
      date="December 8, 2025"
      readTime="11 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics in the human placenta. UNM 2024 study explained"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        A landmark <strong>2024 University of New Mexico study</strong> (Garcia
        et al., <em>Toxicological Sciences</em>) detected microplastics in{' '}
        <strong>100% of 62 human placentas tested</strong>, averaging{' '}
        <strong>~127 micrograms per gram</strong> of placental tissue. The
        dominant polymer was <strong>polyethylene</strong> (54%), followed by
        PVC and nylon. This is the first study to quantify microplastic mass
        in the placenta, earlier 2020 work only confirmed their presence.
        For pregnant women, the takeaway is not panic but priority: filter
        drinking water, eliminate plastic food storage and reheating, reduce
        synthetic cosmetics, and ventilate the home.
      </BlogTLDR>

      <p>
        <strong>Latest research:</strong> this page covers{' '}
        <em>detection</em> — the particles are present in real human placental
        tissue. A{' '}
        <Link href="/blog/nanoplastics-placenta-hormones-study-2026" className="text-forest underline">July 2026 study from Vrije Universiteit Amsterdam</Link>{' '}
        asks the next question, using a three-layer human cell model to test
        whether the particles can <em>cross</em> the placental barrier and
        whether doing so changes hormone production. Four of five polymers
        crossed within 72 hours, and steroid output changed.
      </p>

      <BlogKeyTakeaways
        items={[
          <>The 2024 UNM study found microplastics in every single placenta tested (n=62, 100% positive).</>,
          <>Average concentration: ~127 µg/g of placental tissue, orders of magnitude higher than detected in blood or other tissues.</>,
          <>Polyethylene was the most common polymer found (54%), the plastic used in bags, bottles, and food wrap.</>,
          <>The placenta acts as a filter, particles small enough to cross can reach the developing fetus directly.</>,
          <>The 2021 Ragusa study previously detected microplastics in the placenta but did not quantify mass.</>,
          <>The actions that help most: water filtration, no plastic in microwave, no plastic-stored hot foods, ventilated home.</>,
        ]}
      />

      <KeyStats
        heading="UNM 2024 placenta study, what they found"
        stats={[
          { value: '62 / 62', label: 'placentas tested positive', context: '100% detection rate, n=62', source: 'Garcia et al. 2024 (Toxicological Sciences)', sourceUrl: 'https://academic.oup.com/toxsci/article/197/2/127/7570213' },
          { value: '~127 μg/g', label: 'mean microplastic mass per gram of tissue', context: 'highest concentration measured in any human organ to date', source: 'Garcia et al. 2024', sourceUrl: 'https://academic.oup.com/toxsci/article/197/2/127/7570213' },
          { value: '54%', label: 'of detected polymer was polyethylene', context: 'the plastic in bags, bottles, and food wrap', source: 'Garcia et al. 2024', sourceUrl: 'https://academic.oup.com/toxsci/article/197/2/127/7570213' },
          { value: '12 polymers', label: 'identified across the 62 samples', context: 'PE, PVC, nylon, polypropylene, polyurethane, others', source: 'Garcia et al. 2024', sourceUrl: 'https://academic.oup.com/toxsci/article/197/2/127/7570213' },
          { value: 'Py-GC/MS', label: 'analytical method (pyrolysis GC/MS)', context: 'first study to quantify microplastic mass in placental tissue', source: 'Garcia et al. 2024', sourceUrl: 'https://academic.oup.com/toxsci/article/197/2/127/7570213' },
          { value: '2021', label: 'first detection in placenta (Ragusa et al.)', context: 'qualitative only. 2024 UNM is the first quantitative mass study', source: 'Ragusa et al. 2021 (Environment International)', sourceUrl: 'https://www.sciencedirect.com/science/article/pii/S0160412020322297' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the 2024 UNM placenta study found
      </h2>
      <p>
        Researchers at the University of New Mexico Health Sciences Center
        analyzed 62 human placentas donated after birth. Using a new
        pyrolysis gas chromatography–mass spectrometry (Py-GC/MS) method,
        they could measure not just the presence of microplastics but their
        actual mass per gram of tissue.
      </p>
      <BlogComparisonTable
        caption="UNM 2024 placenta study, headline numbers"
        columns={[
          { key: 'metric', label: 'Metric' },
          { key: 'value', label: 'Result' },
        ]}
        rows={[
          { metric: 'Placentas with detectable microplastics', value: '62 of 62 (100%)' },
          { metric: 'Average concentration', value: '~127 µg/g of tissue' },
          { metric: 'Range across samples', value: '6.5 to 685 µg/g' },
          { metric: 'Most common polymer', value: 'Polyethylene (54%)' },
          { metric: 'Second most common', value: 'PVC (10%)' },
          { metric: 'Other polymers detected', value: 'Nylon, PET, polystyrene, polypropylene' },
          { metric: 'Detection threshold', value: '~7 ng/g' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why this is different from the 2021 Ragusa study
      </h2>
      <p>
        The 2021 Italian study by Antonio Ragusa (<em>Environment
        International</em>) was the first to detect microplastics in human
        placentas, finding particles in 4 of 6 samples. That study used
        Raman spectroscopy and could identify the polymer type but not
        quantify the mass.
      </p>
      <p>
        The UNM 2024 study advanced this in three ways:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Larger sample</strong>. 62 placentas vs 6.</li>
        <li><strong>100% detection</strong>, not 67%, every single one.</li>
        <li><strong>Mass quantification</strong>, we now know roughly how much, not just whether.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How microplastics reach the placenta
      </h2>
      <p>
        The placenta is the organ that connects the developing fetus to the
        mother. Maternal blood flows through it, and small particles can
        cross from the mother into placental tissue (and in some cases
        further, into the fetus). The microplastics found in placental
        tissue likely arrive through:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Inhalation</strong>, indoor air has 3–15× the microplastic load of outdoor air. Inhaled particles cross into blood.</li>
        <li><strong>Ingestion</strong>, bottled water, plastic-packaged food, plastic-reheated meals.</li>
        <li><strong>Skin absorption of chemicals</strong>, phthalates, BPA, parabens from cosmetics, then transferred via blood.</li>
        <li><strong>Maternal blood</strong>, the 2022 Leslie study confirmed microplastics in 77% of healthy human blood samples.</li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What this means for pregnancy
      </h2>
      <p>
        The UNM authors and other reproductive toxicologists emphasize that
        placental microplastics are a marker of exposure, not yet proof of
        harm in humans. But the biological pathways for concern are
        well-established in animal and cell-culture work:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Placental dysfunction</strong>, animal studies show MP exposure reduces placental weight, alters nutrient transport, and increases inflammation.</li>
        <li><strong>Fetal growth restriction</strong>, phthalates and BPA (often carried on microplastics) are linked to lower birth weight in human cohort studies.</li>
        <li><strong>Endocrine disruption</strong>. BPA, BPS, and phthalates interfere with thyroid and sex hormones critical for fetal development.</li>
        <li><strong>Neurodevelopmental impact</strong>, prenatal phthalate exposure is associated with altered cognitive outcomes in children.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical reduction strategy during pregnancy
      </h2>
      <p>
        See our dedicated <Link href="/blog/microplastics-in-pregnancy-by-trimester" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">trimester-by-trimester guide</Link>{' '}
        for week-by-week steps. The top six interventions:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Filter drinking water.</strong> A pitcher certified for microplastics under NSF 401 (PUR PLUS or Brita Elite) or under-sink reverse osmosis removes 99%+ of microplastics in the certified size range.</li>
        <li><strong>Never microwave plastic.</strong> A 2023 study found microwaving plastic food containers releases up to 4.22 million microplastic particles per cm² in 3 minutes.</li>
        <li><strong>Switch to glass food storage.</strong> Pyrex, Anchor Hocking, or Weck jars. Stainless or silicone for travel.</li>
        <li><strong>Drink from glass or stainless, not single-use plastic bottles.</strong> The 2024 Naixin Qian study found bottled water has 240,000 plastic particles per liter.</li>
        <li><strong>Audit cosmetics.</strong> Avoid synthetic fragrance, BHA, BHT, oxybenzone, parabens. See <Link href="/blog/microplastics-in-cosmetics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">cosmetics guide</Link>.</li>
        <li><strong>Ventilate the home.</strong> Open windows; HEPA-filter bedroom and main living area; vacuum twice weekly.</li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What we still don&apos;t know
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>Whether the polyethylene found is from food packaging, water, or air, likely a mix.</li>
        <li>Whether particles cross into fetal tissues at meaningful concentrations in humans.</li>
        <li>The specific dose-response relationship between placental microplastic load and birth outcomes.</li>
        <li>Whether removing exposure during pregnancy reduces placental load by birth (likely partial, given particle persistence).</li>
      </ul>
      <p>
        The 2024 UNM study makes it clear: by the time pregnancy is in
        progress, the placenta is already being exposed. The most useful
        framing is risk reduction, not elimination, every gram of plastic
        avoided is a gram less in the system.
      </p>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-pregnancy" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in pregnancy
        </Link>,{' '}
        <Link href="/blog/microplastics-in-pregnancy-by-trimester" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          trimester-by-trimester guide
        </Link>,{' '}
        <Link href="/blog/microplastics-in-baby-formula" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in baby formula
        </Link>, and{' '}
        <Link href="/blog/microplastics-in-human-blood" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in human blood
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Baby/kid product material, glass, stainless, silicone, polypropylene, PPSU.</>,
          <>Packaging type, jar vs pouch vs multi-layer plastic.</>,
          <>Brand and product line, clean certifications flagged.</>,
          <>Use-context flags you log, sterilization heat, dishwasher cycles, age.</>,
          <>Cited published research behind each 0–100 score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan baby gear and pregnancy products before buying"
        sub="Bottles, sippy cups, baby food pouches, cosmetics. The app weighs material + brand + condition and suggests cleaner-packaged alternatives."
        buttonLabel="Scan baby gear in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Have microplastics really been found in the placenta?',
            answer:
              'Yes. A 2024 University of New Mexico study published in Toxicological Sciences detected microplastics in 100% of 62 human placentas tested, averaging about 127 micrograms per gram of tissue. An earlier 2021 study by Ragusa et al. first confirmed their presence in a smaller sample of 6 placentas.',
          },
          {
            question: 'What kind of plastic is most often found in the placenta?',
            answer:
              'In the UNM 2024 study, polyethylene was the dominant polymer (about 54% of samples), the same plastic used in shopping bags, food wrap, and squeezable bottles. PVC, nylon, PET, polystyrene, and polypropylene were also detected.',
          },
          {
            question: 'Can microplastics cross from the placenta to the baby?',
            answer:
              'Animal studies show that particles smaller than about 100 nanometers can cross the placental barrier into fetal tissues, and some particles up to a few micrometers may also cross under inflammatory conditions. In humans, this has been less directly studied, but the placenta is known to be permeable to many plasticiser chemicals (BPA, phthalates) that travel into fetal blood.',
          },
          {
            question: 'Does microplastic exposure during pregnancy harm the baby?',
            answer:
              'No human study has yet proven direct harm from microplastic particles themselves during pregnancy. However, the chemicals carried by them (BPA, phthalates, PFAS, parabens) have been linked in human cohorts to lower birth weight, altered thyroid function, and neurodevelopmental changes. Animal studies show clearer effects: reduced placental weight, altered nutrient transport, inflammation, fetal growth restriction.',
          },
          {
            question: 'What is the most important thing a pregnant woman can do to reduce exposure?',
            answer:
              'In order of impact: (1) filter drinking water, (2) never microwave food in plastic, (3) switch food storage to glass, (4) avoid bottled water, (5) audit cosmetics for synthetic fragrance and parabens, (6) ventilate the home and HEPA-filter the bedroom.',
          },
          {
            question: 'Is it too late to reduce exposure if I am already pregnant?',
            answer:
              'No. Microplastic and chemical loads turn over partially, reducing exposure during pregnancy reduces what is delivered across the placenta from that point forward. Every reduction matters, especially for the higher-risk second and third trimesters when fetal organ systems are still developing.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Garcia MA, Liu R, Nihart A, et al.',
            year: 2024,
            title: 'Quantitation and identification of microplastics accumulation in human placental specimens using pyrolysis gas chromatography mass spectrometry',
            publisher: 'Toxicological Sciences',
            url: 'https://academic.oup.com/toxsci/article/199/1/81/7609299',
          },
          {
            authors: 'Ragusa A, Svelato A, Santacroce C, et al.',
            year: 2021,
            title: 'Plasticenta: First evidence of microplastics in human placenta',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412020322297',
          },
          {
            authors: 'Leslie HA, van Velzen MJM, Brandsma SH, et al.',
            year: 2022,
            title: 'Discovery and quantification of plastic particle pollution in human blood',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412022001258',
          },
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy',
            publisher: 'PNAS',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
          {
            authors: 'Hussain N, Jaitley V, Florence AT',
            year: 2001,
            title: 'Recent advances in the understanding of uptake of microparticulates across the gastrointestinal lymphatics',
            publisher: 'Advanced Drug Delivery Reviews',
            url: 'https://pubmed.ncbi.nlm.nih.gov/11259839/',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
